import type { Capability } from "@hik/contracts";
import type { IconName } from "../ui/icon-data";

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Görünürlük: herhangi bir yetki, yöneticilik veya çalışan kaydı. */
  show?: (ctx: NavContext) => boolean;
  /** Rozet sayısı anahtarı (ana ekran verisinden). */
  badge?: "approvals" | "tasks" | "documents" | "notifications";
  /** Telefonda alt sekme çubuğunda gösterilsin mi. */
  tab?: boolean;
}

export interface NavGroup {
  label: string | null;
  items: NavItem[];
}

export interface NavContext {
  can: (c: Capability) => boolean;
  isManager: boolean;
  hasEmployee: boolean;
  hasApprovals: boolean;
}

const any = (...caps: Capability[]) => (ctx: NavContext) => caps.some((c) => ctx.can(c));

/** Bilgi mimarisi: docs/UX.md §2. Kişisel bölümler üstte, rol bölümleri başlıklı gruplarda. */
export const NAV: NavGroup[] = [
  {
    label: null,
    items: [
      { to: "/", label: "Bugün", icon: "house", tab: true },
      { to: "/bildirimler", label: "Bildirimler", icon: "bell", badge: "notifications" },
      { to: "/izin", label: "İzinlerim", icon: "calendar-blank", show: (c) => c.hasEmployee, tab: true },
      { to: "/onaylar", label: "Onaylar", icon: "check-circle", show: (c) => c.hasApprovals, badge: "approvals", tab: true },
      { to: "/ekibim", label: "Ekibim", icon: "users-three", show: (c) => c.isManager },
      { to: "/masraf", label: "Masraf ve avans", icon: "receipt", show: (c) => c.hasEmployee },
      { to: "/belgeler", label: "Belgelerim", icon: "files", show: (c) => c.hasEmployee, badge: "documents", tab: true },
      { to: "/bordro", label: "Bordrolarım", icon: "wallet", show: (c) => c.hasEmployee },
      { to: "/zimmetlerim", label: "Zimmetlerim", icon: "package", show: (c) => c.hasEmployee },
      { to: "/gorevler", label: "Görevlerim", icon: "list-checks", badge: "tasks" },
      { to: "/duyurular", label: "Duyurular", icon: "megaphone" },
      { to: "/bilgi", label: "Politikalar", icon: "book-open" },
      { to: "/kisiler", label: "Kişiler", icon: "users" },
    ],
  },
  {
    label: "İK",
    items: [
      { to: "/ik/calisanlar", label: "Çalışanlar", icon: "identification-card", show: any("employees.view_all") },
      { to: "/ik/izin", label: "İzin yönetimi", icon: "calendar-check", show: any("leave.manage", "leave.view_all") },
      { to: "/ik/belgeler", label: "Belgeler", icon: "file-text", show: any("documents.manage") },
      { to: "/ik/bordro", label: "Bordro dağıtımı", icon: "paper-plane-tilt", show: any("payslips.manage") },
      { to: "/ik/surecler", label: "İşe giriş ve çıkış", icon: "arrows-left-right", show: any("checklists.manage") },
      { to: "/ik/zimmet", label: "Zimmet", icon: "package", show: any("assets.manage") },
      { to: "/ik/duyurular", label: "Duyuru yönetimi", icon: "megaphone", show: any("announcements.manage") },
      { to: "/ik/eksikler", label: "Eksikler", icon: "warning-circle", show: any("employees.view_all") },
      // İçe aktarım türleri çalışan ya da izin yönetimi yetkisi ister; yalnız import.run (ör. BT) yetmez.
      { to: "/ik/iceaktar", label: "İçe aktar", icon: "upload-simple", show: (c) => c.can("import.run") && (c.can("employees.manage") || c.can("leave.manage")) },
      { to: "/ik/raporlar", label: "Raporlar", icon: "chart-bar", show: any("reports.view") },
    ],
  },
  {
    label: "Finans",
    items: [{ to: "/finans", label: "Ödemeler", icon: "hand-coins", show: any("expenses.finance") }],
  },
  {
    label: "Yönetim",
    items: [{ to: "/ayarlar", label: "Ayarlar", icon: "gear", show: any("settings.manage", "users.manage", "org.manage", "audit.view", "leave.manage", "documents.manage", "expenses.finance") }],
  },
];

export function visibleNav(ctx: NavContext): NavGroup[] {
  return NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.show || i.show(ctx)) })).filter((g) => g.items.length > 0);
}
